import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AnalyzeTerraformDeploymentDocument,
	type AnalyzeTerraformInput,
	CreateDeploymentDocument,
	type CreateDeploymentInput,
	DeleteDeploymentDocument,
	EnvironmentAwsStacksDocument,
	RefreshDeploymentDocument,
	RequestTerraformUploadDocument,
} from "../artemis-sdk";
import { uploadToPresignedPost } from "../presigned-upload";
import { useGqlClient } from "../use-gql-client";

/**
 * Per-file size cap for Terraform uploads. Mirrors `TerraformSourceReader.MaxFileBytes` on the
 * API, which signs the upload policy with it and re-checks on read — a bigger file is rejected
 * by S3 whatever the UI does, so there is no point requesting a target for one.
 */
export const MAX_TERRAFORM_FILE_BYTES = 8 * 1024 * 1024;

/**
 * Creates a new deployment linking a project to an environment.
 * For AWS environments, pass `awsConfig` with the target stack names.
 */
export function useCreateDeployment() {
	const gqlFetch = useGqlClient();

	const { mutate } = useMutation({
		mutationFn: async (input: CreateDeploymentInput) => {
			const data = await gqlFetch(CreateDeploymentDocument, { input });
			return data.createDeployment.deployment;
		},
	});
	return mutate;
}

/**
 * Triggers infrastructure discovery for a deployment and refreshes its graph.
 * Invalidates the environment query on success so the updated graph is reflected.
 */
export function useRefreshDeployment(environmentId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate, isPending, error } = useMutation({
		mutationFn: async (deploymentId: string) => {
			const data = await gqlFetch(RefreshDeploymentDocument, { deploymentId });
			return data.refreshDeployment.deployment;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["environment", environmentId],
			});
		},
	});
	return { refresh: mutate, isPending, error };
}

/**
 * Permanently deletes a deployment. Invalidates the project query on success.
 */
export function useDeleteDeployment(projectId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutate, isPending } = useMutation({
		mutationFn: async (deploymentId: string) => {
			const data = await gqlFetch(DeleteDeploymentDocument, { deploymentId });
			return data.deleteDeployment.success;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["project", projectId] });
		},
	});
	return { deleteDeployment: mutate, isPending };
}

/**
 * Fetches the list of active CloudFormation stacks from an AWS environment.
 * Only runs when environmentId is provided. Falls back to the environment's
 * default region when region is not specified.
 */
export function useAwsListStacks(
	environmentId: string,
	region?: string | null,
) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["awsListStacks", environmentId, region],
		queryFn: async () => {
			const data = await gqlFetch(EnvironmentAwsStacksDocument, {
				environmentId,
				region,
			});
			return data.environmentAwsStacks ?? [];
		},
		enabled: !!environmentId,
	});
}

/**
 * Uploads Terraform files and builds the deployment's graph from them.
 *
 * The files land in transient storage and are deleted as soon as they have been read, so this is
 * not repeatable on its own: rebuilding the graph means calling it again with the files.
 */
export function useAnalyzeTerraform(projectId: string, workspaceId: string) {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();

	const { mutateAsync, isPending } = useMutation({
		mutationFn: async ({
			deploymentId,
			files,
		}: {
			deploymentId: string;
			files: File[];
		}) => {
			const oversized = files.filter((f) => f.size > MAX_TERRAFORM_FILE_BYTES);

			if (oversized.length) {
				throw new Error(
					`${oversized.map((f) => f.name).join(", ")} exceed the ` +
						`${MAX_TERRAFORM_FILE_BYTES / 1024 / 1024} MB per-file limit. ` +
						"Remove them and try again.",
				);
			}

			const uploadKeys: string[] = [];

			if (files.length) {
				const { requestTerraformUpload } = await gqlFetch(
					RequestTerraformUploadDocument,
					{ workspaceId, filenames: files.map((f) => f.name) },
				);

				// Targets come back in the order the filenames were sent. allSettled, not all:
				// Promise.all reports the first failure while the other uploads are still in
				// flight, so their errors surface as unhandled rejections and the user is told
				// about one bad file out of sixteen.
				const results = await Promise.allSettled(
					requestTerraformUpload.uploads.map((target, i) =>
						uploadToPresignedPost(target.upload, files[i]).then(
							() => target.key,
						),
					),
				);
				const failures: string[] = [];

				for (const result of results) {
					if (result.status === "fulfilled") uploadKeys.push(result.value);
					else
						failures.push(
							result.reason instanceof Error
								? result.reason.message
								: String(result.reason),
						);
				}

				// Analyzing the files that did make it would silently build the graph from a
				// partial picture, so report every failure instead and let the user retry.
				if (failures.length) throw new Error(failures.join("\n"));
			}

			const input: AnalyzeTerraformInput = { uploadKeys };
			const data = await gqlFetch(AnalyzeTerraformDeploymentDocument, {
				deploymentId,
				input,
			});
			return data.analyzeTerraformDeployment.deployment;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["project", projectId] });
		},
	});

	return { analyzeTerraform: mutateAsync, isPending };
}
